// Full-page working-sessions roster ("Sessions" in the left sidebar). One row
// per doctor (or location), Mon–Sun columns, chips = session blocks — the
// editable source of truth that generates the bookable micro-slots.
//
// Two modes:
//   • Template — the recurring weekly rules. Click a chip to edit the block;
//     hover a cell to add one.
//   • Week — the effective roster for the picked week (overrides applied).
//     Click a chip to apply/inspect a per-day override (cancel / move /
//     relocate) without touching the template.
//
// Structure mirrors slots-heatmap-page: the page owns all view state and
// fetches its own doctor/tenant options.

import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { CalendarPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { fetchTenants } from "@/lib/api/tenants";
import { filterClinicsForSurface } from "@/lib/clinic-visibility";
import { fetchDoctors } from "@/lib/api/users";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { deleteSessionBlock } from "@/lib/api/admin-session-blocks";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { useHasPermission } from "@/hooks/use-permissions";
import { useHideSunday } from "@/hooks/use-user-preferences";
import { startOfWeek } from "../calendar-utils";
import { SessionsToolbar } from "./toolbar";
import { RosterMatrix } from "./roster-matrix";
import { SessionBlockFormSheet } from "./session-block-form-sheet";
import { SessionOverrideDialog } from "./override-dialog";
import { useSessionBlockDefinitions, useSessionOccurrences } from "./use-session-blocks";

// Option mappers mirror slots-heatmap-page (standalone page → own fetches).
function toTenantOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const name = resolveTenantDisplayName(item, id);
  const timezone = String(item?.timezone || "").trim();
  return { id, name, timezone };
}

function toDoctorOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const firstName = String(item?.firstName || "").trim();
  const lastName = String(item?.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = String(item?.displayName || "").trim();
  return { id, name: fullName || displayName || id };
}

function SessionsRosterPage() {
  const canManage = useHasPermission("scheduling", "manage");

  const [mode, setMode] = useState("week");
  const [groupBy, setGroupBy] = useState("doctor");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(DateTime.now()));
  const fromDate = useMemo(() => weekStart.startOf("day"), [weekStart]);
  const toDate = useMemo(() => weekStart.plus({ days: 6 }).endOf("day"), [weekStart]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, idx) => weekStart.plus({ days: idx })),
    [weekStart],
  );
  const rangeLabel = `${weekStart.toFormat("d LLL")} → ${weekStart.plus({ days: 6 }).toFormat("d LLL")}`;
  const isCurrentWeek = weekStart.toISODate() === startOfWeek(DateTime.now()).toISODate();
  const goToWeek = (dt) => setWeekStart(startOfWeek(dt));

  const hideSunday = useHideSunday();

  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [blockForm, setBlockForm] = useState({
    open: false,
    mode: "create",
    block: null,
    defaultDoctorId: "",
    defaultTenantId: "",
    defaultWeekday: null,
  });
  const [overrideTarget, setOverrideTarget] = useState(null); // {occurrence, block} | null
  const [deleteTarget, setDeleteTarget] = useState(null); // block | null
  const [isDeleting, setIsDeleting] = useState(false);

  const [doctorOptions, setDoctorOptions] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const didInitTenantFiltersRef = useRef(false);
  const didInitDoctorFiltersRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    Promise.all([fetchTenants(), fetchDoctors()])
      .then(([tenantResponse, doctorResponse]) => {
        if (!isMounted) return;
        // Clinics hidden on the "calendar" surface (Settings → Clinic visibility) stay out.
        const tenants = filterClinicsForSurface(tenantResponse?.items, "calendar")
          .map(toTenantOption)
          .filter(Boolean);
        const doctors = (Array.isArray(doctorResponse?.items) ? doctorResponse.items : [])
          .map(toDoctorOption)
          .filter(Boolean);
        setTenantOptions(tenants);
        setDoctorOptions(doctors);
        if (!didInitTenantFiltersRef.current) {
          setSelectedTenantIds(tenants.map((opt) => opt.id));
          didInitTenantFiltersRef.current = true;
        }
        if (!didInitDoctorFiltersRef.current) {
          setSelectedDoctorIds(doctors.map((opt) => opt.id));
          didInitDoctorFiltersRef.current = true;
        }
        setOptionsError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        setTenantOptions([]);
        setDoctorOptions([]);
        setOptionsError(error);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const { blocks, blockById, isLoading: blocksLoading } = useSessionBlockDefinitions({
    refreshKey,
    doctorOptions,
    tenantOptions,
    enabled: true,
  });
  const {
    occurrences,
    isLoading: occurrencesLoading,
    applyLocalOverride,
    revertLocalOverride,
  } = useSessionOccurrences({
    fromDate,
    toDate,
    refreshKey,
    enabled: mode === "week",
  });

  const bumpRefresh = () => setRefreshKey((value) => value + 1);

  const toggleIn = (setter) => (id) =>
    setter((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const handleChipClick = (payload) => {
    if (payload.kind === "block") {
      if (!canManage) return;
      setBlockForm({
        open: true,
        mode: "edit",
        block: payload.block,
        defaultDoctorId: "",
        defaultTenantId: "",
        defaultWeekday: null,
      });
      return;
    }
    // Week mode: open the per-day override dialog for this occurrence.
    if (!canManage) return;
    setOverrideTarget({
      occurrence: payload.occurrence,
      block: payload.block || blockById.get(payload.occurrence.blockId) || null,
    });
  };

  const handleAddToCell = (rowEntityId, isoWeekday) => {
    setBlockForm({
      open: true,
      mode: "create",
      block: null,
      defaultDoctorId: groupBy === "doctor" ? rowEntityId : "",
      defaultTenantId: groupBy === "tenant" ? rowEntityId : "",
      defaultWeekday: isoWeekday,
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteSessionBlock({ blockId: deleteTarget.id });
      toast.success("Session block deleted", {
        description: "Its generated slots were removed with it.",
      });
      setDeleteTarget(null);
      setBlockForm((current) => ({ ...current, open: false }));
      bumpRefresh();
      publishCalendarRefresh({ source: "session-block-delete" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete the session block.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/40 px-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Calendar</span>
          <span className="text-muted-foreground/60">/</span>
          <span className="font-semibold text-foreground">
            Sessions · {mode === "template" ? "Weekly template" : `Week of ${weekStart.toFormat("d LLL")}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="[&_svg]:size-3.5"
            onClick={bumpRefresh}
          >
            <RefreshCw />
            Refresh
          </Button>
          {canManage ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="[&_svg]:size-3.5"
              onClick={() =>
                setBlockForm({
                  open: true,
                  mode: "create",
                  block: null,
                  defaultDoctorId: "",
                  defaultTenantId: "",
                  defaultWeekday: null,
                })
              }
            >
              <CalendarPlus />
              New session block
            </Button>
          ) : null}
        </div>
      </header>

      <SessionsToolbar
        mode={mode}
        onModeChange={setMode}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        rangeLabel={rangeLabel}
        weekStartDate={weekStart.toJSDate()}
        onPickDate={(date) => goToWeek(DateTime.fromJSDate(date))}
        isCurrentWeek={isCurrentWeek}
        onPrevWeek={() => goToWeek(weekStart.minus({ weeks: 1 }))}
        onNextWeek={() => goToWeek(weekStart.plus({ weeks: 1 }))}
        onToday={() => goToWeek(DateTime.now())}
        tenants={tenantOptions}
        selectedTenantIds={selectedTenantIds}
        onToggleTenant={toggleIn(setSelectedTenantIds)}
        onSelectAllTenants={() => setSelectedTenantIds(tenantOptions.map((opt) => opt.id))}
        onClearTenants={() => setSelectedTenantIds([])}
        doctors={doctorOptions}
        selectedDoctorIds={selectedDoctorIds}
        onToggleDoctor={toggleIn(setSelectedDoctorIds)}
        onSelectAllDoctors={() => setSelectedDoctorIds(doctorOptions.map((opt) => opt.id))}
        onClearDoctors={() => setSelectedDoctorIds([])}
      />

      {optionsError ? (
        <div className="border-b border-border bg-destructive/10 px-5 py-2 text-xs text-destructive">
          Couldn't load doctors and locations — names and filters may be incomplete.
        </div>
      ) : null}

      <RosterMatrix
        mode={mode}
        groupBy={groupBy}
        hideSunday={hideSunday}
        weekDates={weekDates}
        blocks={blocks}
        occurrences={occurrences}
        doctorOptions={doctorOptions}
        tenantOptions={tenantOptions}
        selectedDoctorIds={selectedDoctorIds}
        selectedTenantIds={selectedTenantIds}
        isLoading={mode === "week" ? occurrencesLoading : blocksLoading}
        canManage={canManage}
        onChipClick={canManage ? handleChipClick : undefined}
        onAddToCell={canManage ? handleAddToCell : undefined}
      />

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
        <span>
          {mode === "template"
            ? "Click a chip to edit its recurring rule; hover a cell to add a session."
            : "Click a chip to cancel, move, or relocate that day's session."}
        </span>
        <span>
          {blocks.length} block{blocks.length === 1 ? "" : "s"}
        </span>
      </div>

      <SessionBlockFormSheet
        open={blockForm.open}
        onOpenChange={(next) => setBlockForm((current) => ({ ...current, open: next }))}
        mode={blockForm.mode}
        block={blockForm.block}
        defaultDoctorId={blockForm.defaultDoctorId}
        defaultTenantId={blockForm.defaultTenantId}
        defaultWeekday={blockForm.defaultWeekday}
        doctorOptions={doctorOptions}
        tenantOptions={tenantOptions}
        onSaved={bumpRefresh}
        onDelete={canManage ? (block) => setDeleteTarget(block) : undefined}
      />

      <SessionOverrideDialog
        open={Boolean(overrideTarget)}
        onOpenChange={(next) => {
          if (!next) setOverrideTarget(null);
        }}
        occurrence={overrideTarget?.occurrence || null}
        block={overrideTarget?.block || null}
        tenantOptions={tenantOptions}
        doctorOptions={doctorOptions}
        applyLocalOverride={applyLocalOverride}
        revertLocalOverride={revertLocalOverride}
        onApplied={bumpRefresh}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(next) => {
          if (!next && !isDeleting) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this session block?</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `${deleteTarget.doctorName} · ${deleteTarget.label || ""} ${deleteTarget.startTime}–${deleteTarget.endTime} @ ${deleteTarget.tenantName}. `
                : ""}
              All of its generated bookable slots are removed with it. Booked appointments stay on
              the calendar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" disabled={isDeleting} onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" size="sm" disabled={isDeleting} onClick={confirmDelete}>
              {isDeleting ? <Spinner className="size-3.5" /> : null}
              Delete block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SessionsRosterPage;
